import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { apiErrorMessage } from '../../shared/utils/apiErrorMessage';

const API_URL = process.env.REACT_APP_API_URL;

const field = (err) => ({
  width: '100%', padding: '11px 14px', boxSizing: 'border-box',
  border: `1.5px solid ${err ? '#f5365c' : '#e9ecef'}`,
  borderRadius: '10px', fontSize: '14px', color: '#344767',
  outline: 'none', transition: 'border-color .2s',
});

export default function LeadCaptureForm() {
  const { clientId: routeClientId } = useParams();
  const clientId = routeClientId || new URLSearchParams(window.location.search).get('clientId');

  const [agencyName, setAgencyName] = useState('');
  const [logoUrl, setLogoUrl]   = useState('');
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', contactNumber: '',
    address: '', propertyInterest: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');

  useEffect(() => {
    if (!clientId) return;
    axios.get(`${API_URL}/PublicLead/form-info/${clientId}`)
      .then(r => { setAgencyName(r.data.agencyName || ''); setLogoUrl(r.data.logoUrl || ''); })
      .catch(() => {});
  }, [clientId]);

  const change = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required.';
    if (!form.contactNumber.trim()) e.contactNumber = 'Contact number is required.';
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true); setServerErr('');
    try {
      await axios.post(`${API_URL}/PublicLead/submit/${clientId}`, {
        ...form, source: 'WebForm',
      });
      setSubmitted(true);
    } catch (err) {
      setServerErr(apiErrorMessage(err, 'Submission failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const wrap = {
    minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f0fe 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
  };
  const card = {
    background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '520px',
    boxShadow: '0 8px 40px rgba(52,71,103,0.13)', overflow: 'hidden',
  };
  const lbl = {
    display: 'block', fontSize: '12px', fontWeight: '700', color: '#8392ab',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
  };
  const errMsg = { color: '#f5365c', fontSize: '12px', marginTop: '4px' };

  if (!clientId) {
    return (
      <div style={wrap}>
        <div style={{ ...card, padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '40px', color: '#ffd600', marginBottom: '16px', display: 'block' }} />
          <p style={{ color: '#344767', fontWeight: '700', fontSize: '16px', margin: '0 0 8px' }}>Invalid Form URL</p>
          <p style={{ color: '#8392ab', fontSize: '14px', margin: 0 }}>No client ID found in the URL.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={wrap}>
        <div style={{ ...card, padding: '48px 40px', textAlign: 'center' }}>
          {logoUrl && <img src={logoUrl} alt={agencyName} style={{ height: '52px', objectFit: 'contain', marginBottom: '20px' }} />}
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <i className="fas fa-check" style={{ color: '#2dce89', fontSize: '28px' }} />
          </div>
          <p style={{ fontWeight: '800', fontSize: '20px', color: '#344767', margin: '0 0 8px' }}>Thank you!</p>
          <p style={{ color: '#8392ab', fontSize: '15px', margin: 0 }}>
            We have received your enquiry. Our team will get back to you shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)', padding: '28px 32px', textAlign: 'center' }}>
          {logoUrl && (
            <img src={logoUrl} alt={agencyName}
              style={{ height: '44px', objectFit: 'contain', marginBottom: '12px', display: 'block', margin: '0 auto 12px' }}
            />
          )}
          <p style={{ color: '#fff', fontWeight: '800', fontSize: '18px', margin: '0 0 4px' }}>
            {agencyName || 'Contact Us'}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px', margin: 0 }}>
            Fill in your details and we'll get back to you soon
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submit} style={{ padding: '28px 32px' }}>
          {serverErr && (
            <div style={{ background: '#fff5f5', border: '1px solid #f5365c', borderRadius: '10px', padding: '12px 16px', marginBottom: '18px', color: '#f5365c', fontSize: '13px' }}>
              <i className="fas fa-exclamation-circle" style={{ marginRight: '6px' }} />{serverErr}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={lbl}>First Name *</label>
              <input name="firstName" value={form.firstName} onChange={change}
                style={field(!!errors.firstName)} placeholder="John" />
              {errors.firstName && <p style={errMsg}>{errors.firstName}</p>}
            </div>
            <div>
              <label style={lbl}>Last Name</label>
              <input name="lastName" value={form.lastName} onChange={change}
                style={field(false)} placeholder="Doe" />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Phone Number *</label>
            <input name="contactNumber" value={form.contactNumber} onChange={change}
              style={field(!!errors.contactNumber)} placeholder="+91 98765 43210" type="tel" />
            {errors.contactNumber && <p style={errMsg}>{errors.contactNumber}</p>}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Email Address</label>
            <input name="email" value={form.email} onChange={change}
              style={field(!!errors.email)} placeholder="john@example.com" type="email" />
            {errors.email && <p style={errMsg}>{errors.email}</p>}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Property Interest</label>
            <input name="propertyInterest" value={form.propertyInterest} onChange={change}
              style={field(false)} placeholder="e.g. 2BHK flat in Bandra" />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={lbl}>Address / Location</label>
            <input name="address" value={form.address} onChange={change}
              style={field(false)} placeholder="City, State" />
          </div>

          <button type="submit" disabled={submitting} style={{
            width: '100%', padding: '13px', background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
            border: 'none', borderRadius: '12px', color: '#fff', fontSize: '15px', fontWeight: '700',
            cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.75 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            boxShadow: '0 4px 16px rgba(52,71,103,0.28)',
          }}>
            <i className={submitting ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'} />
            {submitting ? 'Submitting…' : 'Send Enquiry'}
          </button>

          <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: '#adb5bd' }}>
            Your information is safe and will not be shared.
          </p>
        </form>
      </div>
    </div>
  );
}
